import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Clear as ClearIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { searchCustomers, createCustomer, Customer, CustomerInput } from '../firebase/services/customerVesselService';
import { formatPhoneNumber } from '../utils/formatters';

interface CustomerSelectorProps {
  onCustomerSelect: (customer: Customer) => void;
  onCustomerCreate: (customerData: CustomerInput) => void;
  initialSearchTerm?: string;
  selectedCustomer?: Customer | null;
}

interface CustomerFormData {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phone: string;
}

const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  onCustomerSelect,
  onCustomerCreate,
  initialSearchTerm = '',
  selectedCustomer = null
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Debounced search function
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const results = await searchCustomers(term);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching customers:', err);
      setError('Failed to search customers. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== initialSearchTerm || searchTerm.length > 0) {
        performSearch(searchTerm);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, performSearch, initialSearchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCustomerSelect = (customer: Customer) => {
    onCustomerSelect(customer);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleFormChange = (field: keyof CustomerFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const formatted = formatPhoneNumber(value);
    handleFormChange('phone', formatted);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCustomer = async () => {
    if (!validateForm()) {
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Check if customer already exists
      const existingCustomers = await searchCustomers(formData.email);
      const existingCustomer = existingCustomers.find(
        c => c.email.toLowerCase() === formData.email.toLowerCase()
      );

      if (existingCustomer) {
        setError('A customer with this email already exists. Please select them from the search results or use a different email.');
        setCreating(false);
        return;
      }

      const customerData: CustomerInput = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        companyName: formData.companyName.trim() || undefined,
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        vessels: []
      };

      await onCustomerCreate(customerData);
      
      // Reset form and close dialog
      setFormData({
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: ''
      });
      setFormErrors({});
      setShowCreateForm(false);
      
      // Refresh search results to show the new customer
      if (searchTerm) {
        performSearch(searchTerm);
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to create customer. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    setFormData({
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      phone: ''
    });
    setFormErrors({});
    setError(null);
  };

  const prefillFormFromSearch = () => {
    if (searchTerm.includes('@')) {
      // If search term looks like an email, prefill email
      setFormData(prev => ({ ...prev, email: searchTerm }));
    } else {
      // If search term looks like a name, try to split it
      const parts = searchTerm.trim().split(' ');
      if (parts.length >= 2) {
        setFormData(prev => ({
          ...prev,
          firstName: parts[0],
          lastName: parts.slice(1).join(' ')
        }));
      } else if (parts.length === 1) {
        setFormData(prev => ({ ...prev, firstName: parts[0] }));
      }
    }
  };

  return (
    <Box>
      {selectedCustomer ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Selected Customer
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {selectedCustomer.firstName} {selectedCustomer.lastName}
                  {selectedCustomer.companyName && (
                    <Chip label={selectedCustomer.companyName} size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomer.email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatPhoneNumber(selectedCustomer.phone)}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  onCustomerSelect(null);
                  setSearchTerm('');
                }}
              >
                Change
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            Customer Information
          </Typography>
          
          <TextField
            fullWidth
            label="Search for existing customer"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Enter name, email, or phone number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {searchTerm && !loading && (
            <Paper sx={{ mb: 2 }}>
              {searchResults.length > 0 ? (
                <>
                  <Box sx={{ p: 2, bgcolor: 'primary.light', color: 'white' }}>
                    <Typography variant="subtitle1">
                      Found {searchResults.length} customer{searchResults.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <List disablePadding>
                    {searchResults.map((customer, index) => (
                      <React.Fragment key={customer.id}>
                        <ListItem>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1">
                                {customer.firstName} {customer.lastName}
                                {customer.companyName && (
                                  <Chip 
                                    label={customer.companyName} 
                                    size="small" 
                                    sx={{ ml: 1 }} 
                                  />
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {customer.email}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatPhoneNumber(customer.phone)}
                              </Typography>
                            </Box>
                          </Box>
                          <ListItemSecondaryAction>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              Select
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < searchResults.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    No customers found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    No customers match your search criteria.
                  </Typography>
                </Box>
              )}
              
              <Divider />
              <Box sx={{ p: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    prefillFormFromSearch();
                    setShowCreateForm(true);
                  }}
                >
                  Create New Customer
                </Button>
              </Box>
            </Paper>
          )}
        </>
      )}
      {/* Create Customer Dialog */}
      <Dialog 
        open={showCreateForm} 
        onClose={handleCloseCreateForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Customer</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleFormChange('firstName', e.target.value)}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleFormChange('lastName', e.target.value)}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name (Optional)"
                value={formData.companyName}
                onChange={(e) => handleFormChange('companyName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handlePhoneChange}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                placeholder="(xxx) xxx-xxxx"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateForm} disabled={creating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCustomer} 
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creating ? 'Creating...' : 'Create Customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerSelector; 